import type { ClickReleaseConfig } from '../config/types';
import type { ReleaseEntry } from '../content/types';
import { el } from '../util/dom';
import { enter } from '../anim/transition';
import { renderChangelogBody, type ChangelogBodyOptions, type LoadFullEntry } from './changelog';

/**
 * The changelog surface. Renders as a centered modal or a slide-in sidebar depending on
 * `config.surface` — both share identical content and lifecycle, differing only in CSS.
 */
export class ChangelogPanel {
  #root: ShadowRoot;
  #onClose: () => void;
  #overlay: HTMLElement | null = null;
  #panel: HTMLElement | null = null;
  #open = false;
  #prevActive: Element | null = null;

  constructor(root: ShadowRoot, onClose: () => void) {
    this.#root = root;
    this.#onClose = onClose;
  }

  get isOpen(): boolean {
    return this.#open;
  }

  open(entries: ReleaseEntry[], config: ClickReleaseConfig, loadFull?: LoadFullEntry, options?: ChangelogBodyOptions): void {
    if (this.#open) return;
    this.#open = true;
    this.#prevActive = this.#root.activeElement ?? document.activeElement;

    const overlay = el('div', { class: `cr-overlay cr-overlay--${config.surface}`, role: 'presentation' });
    const backdrop = el('div', { class: 'cr-overlay__backdrop' });
    const positioner = el('div', { class: 'cr-overlay__positioner' });
    const panel = el('section', {
      class: 'cr-panel',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': config.title,
      tabindex: '-1',
    });

    const head = el('header', { class: 'cr-panel__head' });
    head.append(el('h2', { class: 'cr-panel__title' }, [config.title]));
    const closeBtn = el('button', { class: 'cr-panel__close', type: 'button', 'aria-label': 'Close' }, ['×']);
    closeBtn.addEventListener('click', () => this.close());
    head.append(closeBtn);

    const body = el('div', { class: 'cr-panel__body' });
    body.append(renderChangelogBody(entries, config, loadFull, options));

    panel.append(head, body);
    positioner.append(panel);
    overlay.append(backdrop, positioner);

    backdrop.addEventListener('click', () => this.close());
    positioner.addEventListener('click', event => {
      if (event.target === positioner) this.close();
    });
    overlay.addEventListener('keydown', this.#onKeydown);

    this.#root.append(overlay);
    this.#overlay = overlay;
    this.#panel = panel;

    enter(overlay);
    requestAnimationFrame(() => panel.focus());
  }

  close(): void {
    if (!this.#open || !this.#overlay) return;
    this.#open = false;

    const overlay = this.#overlay;
    const panel = this.#panel;
    this.#overlay = null;
    this.#panel = null;

    overlay.removeEventListener('keydown', this.#onKeydown);
    overlay.classList.remove('is-open');

    const remove = () => overlay.remove();
    if (panel) {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        panel.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
        remove();
      };
      const onEnd = (event: TransitionEvent) => {
        if (event.target === panel) finish();
      };
      panel.addEventListener('transitionend', onEnd);
      const timer = setTimeout(finish, 400);
    } else {
      remove();
    }

    if (this.#prevActive instanceof HTMLElement) this.#prevActive.focus();
    this.#prevActive = null;
    this.#onClose();
  }

  destroy(): void {
    this.#overlay?.remove();
    this.#overlay = null;
    this.#panel = null;
    this.#open = false;
  }

  #onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
    }
  };
}
